import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { cn } from '../ui/utils'
import {
  isThemePreference,
  resolveThemePreference,
  type ThemePreference,
} from './contract'

export type { ThemePreference } from './contract'

export interface ThemeSwitcherLabels {
  group: string
  system: string
  light: string
  dark: string
}

export interface ThemeSwitcherClasses {
  root?: string
  option?: string
  activeOption?: string
  inactiveOption?: string
}

export interface ThemeSwitcherProps {
  storageKey: string
  labels?: Partial<ThemeSwitcherLabels>
  className?: string
  classes?: ThemeSwitcherClasses
}

const DEFAULT_THEME_SWITCHER_LABELS: ThemeSwitcherLabels = {
  group: 'Тема оформления',
  system: 'Системная тема',
  light: 'Светлая тема',
  dark: 'Тёмная тема',
}


function systemPrefersDark() {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function readStoredThemePreference(storageKey: string): ThemePreference {
  if (typeof window === 'undefined') {
    return 'system'
  }

  try {
    const stored = window.localStorage.getItem(storageKey)
    return isThemePreference(stored) ? stored : 'system'
  } catch {
    return 'system'
  }
}

export function applyThemePreference(preference: ThemePreference) {
  if (typeof document === 'undefined') {
    return
  }

  const resolved = resolveThemePreference(preference, systemPrefersDark())
  document.documentElement.classList.toggle('dark', resolved === 'dark')
  document.documentElement.dataset.themePreference = preference
  document.documentElement.style.colorScheme = resolved
}

const THEME_PREFERENCE_EVENT = 'saas-theme-preference-change'

interface ThemePreferenceEventDetail {
  storageKey: string
  preference: ThemePreference
}

export function useStoredThemePreference(storageKey: string) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => (
    readStoredThemePreference(storageKey)
  ))

  useEffect(() => {
    const syncPreference = (next: ThemePreference) => {
      setPreferenceState(next)
      applyThemePreference(next)
    }
    const handlePreferenceEvent = (event: Event) => {
      const detail = (event as CustomEvent<ThemePreferenceEventDetail>).detail
      if (
        detail?.storageKey === storageKey
        && isThemePreference(detail.preference)
      ) syncPreference(detail.preference)
    }
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== storageKey) return
      syncPreference(isThemePreference(event.newValue) ? event.newValue : 'system')
    }

    window.addEventListener(THEME_PREFERENCE_EVENT, handlePreferenceEvent)
    window.addEventListener('storage', handleStorage)
    return () => {
      window.removeEventListener(THEME_PREFERENCE_EVENT, handlePreferenceEvent)
      window.removeEventListener('storage', handleStorage)
    }
  }, [storageKey])

  useEffect(() => {
    applyThemePreference(preference)

    if (
      preference !== 'system'
      || typeof window === 'undefined'
      || typeof window.matchMedia !== 'function'
    ) {
      return undefined
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const handleSystemThemeChange = () => applyThemePreference('system')

    media.addEventListener('change', handleSystemThemeChange)
    return () => media.removeEventListener('change', handleSystemThemeChange)
  }, [preference])

  const setPreference = useCallback((next: ThemePreference) => {
    try {
      window.localStorage.setItem(storageKey, next)
    } catch {
      // Theme still applies for the current session when storage is unavailable.
    }
    setPreferenceState(next)
    applyThemePreference(next)
    window.dispatchEvent(new CustomEvent<ThemePreferenceEventDetail>(THEME_PREFERENCE_EVENT, {
      detail: { storageKey, preference: next },
    }))
  }, [storageKey])

  return useMemo(
    () => ({
      preference,
      setPreference,
    }),
    [preference, setPreference],
  )
}

export function ThemeSwitcher({
  storageKey,
  labels,
  className,
  classes,
}: ThemeSwitcherProps) {
  const { preference, setPreference } = useStoredThemePreference(storageKey)
  const switcherLabels = { ...DEFAULT_THEME_SWITCHER_LABELS, ...labels }

  return (
    <div
      aria-label={switcherLabels.group}
      className={cn(
        'inline-grid h-8 grid-cols-3 rounded-md border bg-card p-0.5 shadow-sm',
        className,
        classes?.root,
      )}
      role="group"
    >
      <ThemeButton
        active={preference === 'system'}
        label={switcherLabels.system}
        onClick={() => setPreference('system')}
        classes={classes}
      >
        <SystemIcon />
      </ThemeButton>
      <ThemeButton
        active={preference === 'light'}
        label={switcherLabels.light}
        onClick={() => setPreference('light')}
        classes={classes}
      >
        <SunIcon />
      </ThemeButton>
      <ThemeButton
        active={preference === 'dark'}
        label={switcherLabels.dark}
        onClick={() => setPreference('dark')}
        classes={classes}
      >
        <MoonIcon />
      </ThemeButton>
    </div>
  )
}

interface ThemeButtonProps {
  active: boolean
  children: ReactNode
  classes?: ThemeSwitcherClasses
  label: string
  onClick: () => void
}

function ThemeButton({ active, children, classes, label, onClick }: ThemeButtonProps) {
  return (
    <button
      aria-label={label}
      aria-pressed={active}
      className={cn(
        'flex h-7 w-7 items-center justify-center rounded-[5px] text-muted-foreground transition-colors',
        'hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active && 'bg-foreground text-background shadow-sm hover:bg-foreground hover:text-background',
        classes?.option,
        active ? classes?.activeOption : classes?.inactiveOption,
      )}
      onClick={onClick}
      title={label}
      type="button"
    >
      {children}
    </button>
  )
}

function SystemIcon() {
  return (
    <svg
      aria-hidden="true"
      className="size-4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <rect height="14" rx="2" width="20" x="2" y="3" />
      <path d="M8 21h8" />
      <path d="M12 17v4" />
    </svg>
  )
}

function SunIcon() {
  return (
    <svg
      aria-hidden="true"
      className="size-4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg
      aria-hidden="true"
      className="size-4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M20.99 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.78 9.79Z" />
    </svg>
  )
}
